import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, catchError, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { AuthDto, ForgottenPasswordDto, JoinDto } from '@oracychampions/api-interfaces';
import LogRocket from 'logrocket';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  auth$ = new BehaviorSubject<AuthDto | null>(null);

  constructor() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const auth = localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')!) : null;

    if (auth) {
      LogRocket.identify(auth.user.id, {
        name: `${auth.user.firstName} ${auth.user.lastName}`,
        schoolId: auth.user.schoolId,
      });
    }

    this.auth$ = new BehaviorSubject(auth);
    this.auth$.subscribe((auth) => {
      if (auth) {
        localStorage.setItem('auth', JSON.stringify(auth));
      } else {
        localStorage.removeItem('auth');
      }
    });
  }

  login(email: string, password: string): Observable<AuthDto> {
    const body = {
      email,
      password,
    };
    return this.http.post<AuthDto>(`${environment.API}/auth/login`, body).pipe(
      tap((tokens) => {
        LogRocket.identify(tokens.user.id, {
          name: `${tokens.user.firstName} ${tokens.user.lastName}`,
          schoolId: tokens.user.schoolId,
        });
        this.auth$.next(tokens);
      }),
    );
  }

  forgottenPassword(email: string): Observable<any> {
    const body = {
      email,
    };
    return this.http.post<ForgottenPasswordDto>(`${environment.API}/auth/forgotten-password`, body);
  }

  resetPassword(email: string, verificationCode: string, newPassword: string): Observable<any> {
    const body = {
      email,
      verificationCode,
      newPassword,
    };
    return this.http.post<ForgottenPasswordDto>(`${environment.API}/auth/reset-password`, body);
  }

  join(joinData: JoinDto): Observable<any> {
    return this.http.post(`${environment.API}/auth/join`, joinData);
  }

  refreshToken(): Observable<AuthDto> {
    return this.http
      .post<AuthDto>(`${environment.API}/auth/refresh`, {
        refreshToken: this.auth$.getValue()?.refreshToken,
      })
      .pipe(
        catchError((err) => {
          this.router.navigate(['/login']);
          throw new Error(err.message);
        }),
        tap((result: AuthDto) => {
          this.auth$.next(result);
        }),
      );
  }

  logout() {
    this.auth$.next(null);

    this.router.navigate(['/login']);
  }
}
